import { IconButton, IconButtonProps, Tooltip } from '@mui/material'
import { Device, Project, RuuviTag } from '../types'
import { AlertGroup, AlertTemplate } from '../types/Alerts'
import { useTranslation } from 'react-i18next'
import useDialog from '../hooks/useDialog'
import { Delete } from '@mui/icons-material'
import { useState } from 'react'
import { usePolicy } from '../contexts/PolicyContext'
import AlertRuleRemoveDialog from './AlertRuleRemoveDialog'
import { AlertRuleDataGridRow } from '../types/AlertRuleDataGridRow'

export interface AlertRuleRemoveButtonProps extends Omit<IconButtonProps, 'children' | 'onClick'> {
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  alertRules: AlertGroup[]
  alertTemplates: AlertTemplate[]
  rule: string
  useRedux?: boolean
  beforeUpdate?: (input: AlertRuleDataGridRow[]) => void
  afterUpdate?: (alertRules: AlertGroup[]) => void
}

export default function AlertRuleRemoveButton({
  project,
  ruuviTag,
  device,
  alertRules,
  alertTemplates,
  rule,
  useRedux = true,
  beforeUpdate,
  afterUpdate,
  ...props
}: AlertRuleRemoveButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The policy state.
   */
  const { canEditProjectAlertRules, canEditDeviceAlertRules, canEditRuuviTagAlertRules } =
    usePolicy()

  /**
   * Indicates if the current user can remove an alert rule.
   */
  const canRemoveAlertRule =
    (project && canEditProjectAlertRules(project)) ||
    (device && canEditDeviceAlertRules(device)) ||
    (ruuviTag && canEditRuuviTagAlertRules(ruuviTag))

  /**
   * Indicates if the alert rule is being deleted.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * Handle before submit.
   */
  function handleBeforeSubmit(input: AlertRuleDataGridRow[]) {
    beforeUpdate?.(input)
    setSubmitting(true)
  }

  /**
   * Handle after submit.
   */
  function handleAfterSubmit(alertRules: AlertGroup[]) {
    afterUpdate?.(alertRules)
    setSubmitting(false)
    handleClose()
  }

  /**
   * Handle close.
   */
  function handleClose() {
    setSubmitting(false)
    closeDialog()
  }

  return (
    <>
      {canRemoveAlertRule && (
        <Tooltip title={t('delete')}>
          <IconButton
            color="error"
            size="small"
            {...props}
            disabled={isSubmitting}
            onClick={openDialog}
          >
            <Delete />
          </IconButton>
        </Tooltip>
      )}

      <AlertRuleRemoveDialog
        rule={rule}
        alertRules={alertRules}
        alertTemplates={alertTemplates}
        project={project}
        device={device}
        ruuviTag={ruuviTag}
        open={open}
        useRedux={useRedux}
        onClose={handleClose}
        beforeSubmit={handleBeforeSubmit}
        afterSubmit={handleAfterSubmit}
      />
    </>
  )
}
